import { useState } from "react";
import { Gift, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/components/Bits";

const TIERS = [["pro", "Pro (£79 plan)"], ["club", "Base (£49 plan)"]];

const FreeDialog = ({ m, open, onOpenChange, onSaved }) => {
  const fa = m.free_access || {};
  const [f, setF] = useState({ enabled: !!m.free_active, tier: fa.tier || "pro", until: fa.until ? fa.until.slice(0, 10) : "", note: fa.note || "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/members/${m.id}/free-access`, { ...f, until: f.until || null });
      onSaved(data); onOpenChange(false);
      toast.success(f.enabled ? `${m.name} now has free ${f.tier === "pro" ? "Pro" : "Base"} access` : `Free access switched off for ${m.name}${data.tester_code && !m.tester_code ? (data.tester_code.emailed ? `. Their personal code ${data.tester_code.code} has been emailed.` : `. Their code ${data.tester_code.code} couldn't be emailed just now, so I'll retry tomorrow. You can also share it by hand.`) : ""}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`free-dialog-${m.id}`}>
        <DialogHeader>
          <DialogTitle>Free access for {m.name}</DialogTitle>
          <DialogDescription>For partners, press and friends. They're never charged while it's on.{m.beta_tester && !m.tester_code && " Beta testers are emailed a personal 20% code, valid 30 days, when you switch it off."}</DialogDescription>
        </DialogHeader>
        <label className="flex items-center justify-between gap-3 text-sm font-medium">Give free access
          <Switch checked={f.enabled} onCheckedChange={(v) => setF({ ...f, enabled: v })} data-testid="free-enabled-switch" />
        </label>
        {f.enabled && (
          <div className="space-y-4">
            <div className="flex gap-2">{TIERS.map(([k, label]) => (
              <button key={k} type="button" onClick={() => setF({ ...f, tier: k })} data-testid={`free-tier-${k}`}
                className={`flex-1 rounded-xl border px-3 py-2 text-sm transition-colors ${f.tier === k ? "bg-navy text-white border-navy" : "border-border hover:border-navy"}`}>{label}</button>
            ))}</div>
            <label className="block text-sm">End date (optional, leave blank to keep it on until you switch it off)
              <Input type="date" value={f.until} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setF({ ...f, until: e.target.value })} data-testid="free-until-input" className="mt-1" />
            </label>
            <label className="block text-sm">Note for yourself (optional)
              <Input value={f.note} maxLength={200} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="e.g. Press: Food Magazine feature" data-testid="free-note-input" className="mt-1" />
            </label>
          </div>
        )}
        {m.tester_code && <p className="text-xs text-muted-foreground" data-testid="free-tester-code">Tester code {m.tester_code.code}, valid until {fmtDate(m.tester_code.expires_at)}.</p>}
        <Button onClick={save} disabled={busy} data-testid="free-save-btn" className="rounded-full bg-navy hover:bg-navy/90 text-white">
          {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export const FreeAccessCell = ({ m, onChange }) => {
  const [open, setOpen] = useState(false);
  const fa = m.free_access;
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid={`member-free-btn-${m.id}`}
        className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-mono uppercase transition-colors ${m.free_active ? "bg-leaf/15 text-navy" : "text-muted-foreground hover:text-pinkb"}`}>
        <Gift className="h-3 w-3" />{m.free_active ? `Free ${fa.tier === "pro" ? "Pro" : "Base"}${fa.until ? ` to ${fmtDate(fa.until)}` : ""}` : "Give free access"}
      </button>
      {open && <FreeDialog m={m} open={open} onOpenChange={setOpen} onSaved={(d) => onChange({ ...m, ...d })} />}
    </>
  );
};
